<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import { useGetters } from "@/store/vuex-composition-mappers";

//type CreateBlockPayload = Pick<PageSection, "type" | "typeId" | "dataValues" | "styleValues">;

const props = defineProps({
    blocks: {
        type: Array,
        required: true,
    },
});

const { pageColors, pageColorsStyles } = useGetters("page/theme", [
    "pageColors",
    "pageColorsStyles",
]);
const emit = defineEmits(["dragStart"]);

const previewRefs = ref([]);
const containerRefs = ref([]);
const scales = ref([]);
const componentsLoaded = ref(false);
const componentLoadingStates = ref(new Array(props.blocks.length).fill(true));

function getPreviewProps(block) {
    return {
        block: {
            dataValues: block.dataFields.reduce((acc, field) => {
                acc[field.value] = field.defaultData;
                return acc;
            }, {}),
            styleValues: block.styleFields.reduce((acc, field) => {
                acc[field.value] = field.default;
                return acc;
            }, {}),
        },
        pageColors,
    };
}

function calculateScale(index) {
    const previewEl = previewRefs.value[index];
    const containerEl = containerRefs.value[index];

    if (!previewEl || !containerEl) return;

    // Get the actual width of the preview container
    const previewWidth = previewEl.offsetWidth;
    // Desktop width we want to simulate
    const desktopWidth = 1280;

    // Calculate scale based on preview width
    const scale = Math.max(0.1, previewWidth / desktopWidth);
    scales.value[index] = scale;

    // Set the container height based on the scaled content
    const actualHeight = containerEl.scrollHeight;
    if (actualHeight > 0) {
        previewEl.style.height = `${actualHeight * scale}px`;
        // Mark component as loaded after scale is calculated
        componentLoadingStates.value[index] = false;
    }
}

async function recalculateAllScales() {
    // Reset loading states
    componentLoadingStates.value = new Array(props.blocks.length).fill(true);

    // Wait for the next tick to ensure refs are updated
    await nextTick();

    // Reset the scales array to match blocks length
    scales.value = new Array(props.blocks.length).fill(0.3);

    setTimeout(() => {
        previewRefs.value.forEach((_, index) => {
            calculateScale(index);
        });
        componentsLoaded.value = true;
    }, 200);
}

// Watch for ANY changes in blocks
watch(
    () => props.blocks,
    async () => {
        // Reset loading states for all components
        componentLoadingStates.value = new Array(props.blocks.length).fill(true);
        componentsLoaded.value = false;

        // Clear existing refs and scales
        previewRefs.value = [];
        containerRefs.value = [];
        scales.value = new Array(props.blocks.length).fill(0.3);

        // Wait for DOM update
        await nextTick();

        // Recalculate all scales
        await recalculateAllScales();
    },
    { deep: true, immediate: true }
);

// Watch for component mounting state
watch(componentsLoaded, async (isLoaded) => {
    if (!isLoaded) {
        await recalculateAllScales();
    }
});

// Debounce the resize handler
let resizeTimeout;
function handleResize() {
    if (resizeTimeout) {
        clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(async () => {
        await recalculateAllScales();
    }, 100);
}

// const createBlock = async (block: BlockDef) => {
//     const payload: CreateBlockPayload = {
//         type: block.type,
//         typeId: block.typeId,
//         dataValues: {},
//         styleValues: {},
//     };

//     logger.debug("createBlock: ", JSON.parse(JSON.stringify(block)));

//     block.dataFields.forEach((field) => {
//         payload.dataValues[field.value] = field.defaultData;
//     });
//     block.styleFields.forEach((field) => {
//         payload.styleValues[field.value] = field.default;
//     });

//     const newSection = await createBlockSection(payload);
//     mode.value = SIDEBAR_MODES.createBlock;
//     currentSection.value = newSection;
// };

function handleDragStart(event, block) {
    event.dataTransfer.setData(
        "new-easypage-section",
        JSON.stringify({
            type: "block",
            data: block,
        })
    );
    event.dataTransfer.effectAllowed = "copy";

    emit("dragStart");
}

onMounted(async () => {
    window.addEventListener("resize", handleResize);
    componentsLoaded.value = false;
    await recalculateAllScales();
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", handleResize);
    if (resizeTimeout) {
        clearTimeout(resizeTimeout);
    }
});
</script>

<template>
    <div class="p-6 flex flex-col gap-10">
        <div
            v-for="(block, index) in blocks"
            :key="block.type + index"
            class="flex items-center flex-col gap-4 ct-ring-1 hover:ct-ring-brand cursor-move transition-all duration-200"
            draggable="true"
            @dragstart="(e) => handleDragStart(e, block)"
        >
            <div
                v-if="block.component"
                :ref="
                    (el) => {
                        previewRefs[index] = el;
                    }
                "
                class="w-full overflow-hidden border border-gray-300 rounded-xl relative"
                :class="{ 'h-[300px]': componentLoadingStates[index] }"
            >
                <!-- Loading skeleton -->
                <div
                    v-if="componentLoadingStates[index]"
                    class="absolute inset-0 bg-gray-100 animate-pulse"
                >
                    <div class="h-full w-full flex items-center justify-center">
                        <div
                            class="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"
                        ></div>
                    </div>
                </div>

                <!-- Content with transition -->
                <div
                    :ref="
                        (el) => {
                            containerRefs[index] = el;
                        }
                    "
                    class="w-[1280px] transition-all duration-300"
                    :class="{
                        'opacity-0 invisible': componentLoadingStates[index],
                        'opacity-100 visible': !componentLoadingStates[index],
                    }"
                    :style="{
                        transform: `scale(${scales[index] || 0.3})`,
                        transformOrigin: 'top left',
                        ...pageColorsStyles,
                    }"
                >
                    <component
                        :is="block.component"
                        v-bind="getPreviewProps(block)"
                        @vue:mounted="() => calculateScale(index)"
                    />
                </div>
            </div>
            <div class="text-sm overflow-hidden">
                <p class="font-medium ct-text-1 truncate">{{ block.name }}</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}

.animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}
</style>
